/**
 * Die Spalten, die eine Ticketliste zeigen kann -- F1-30, K-30.
 *
 * **Deckungsgleich mit der Erlaubnisliste des Servers** (`VIEW_COLUMNS` in
 * `shortcuts/schemas.py`). Eine Spalte, die es hier gibt und dort nicht,
 * liesse sich zwar anzeigen, aber nicht in einer Ansicht speichern -- und der
 * Anwender bekaeme beim Speichern einen Fehler zu sehen, dessen Ursache er
 * nicht erkennen kann.
 *
 * Hier stehen nur die Spalten, die `TicketListItem` auch liefert. Die
 * restlichen Werte der Erlaubnisliste -- `responsible`, `source`,
 * `resolved_at`, `closed_at`, `type` -- gehoeren zur Einzelansicht; sie kaemen
 * in der Liste je Zeile eine Abfrage teuer.
 *
 * `sla` gehoert dagegen in die Liste (F4-08): Die Restzeit kommt mit der
 * Antwort mit und kostet keine Abfrage je Zeile -- der Server laedt den
 * Kalender je Zusage einmal.
 */
export const VIEW_COLUMNS = [
  'ticket_number',
  'title',
  'customer',
  'state',
  'priority',
  'queue',
  'category',
  'owner',
  'created_at',
  'updated_at',
  'is_major_incident',
  'sla',
  'awaiting_reply',
] as const;

export type ViewColumn = (typeof VIEW_COLUMNS)[number];

/**
 * Was ohne eigene Wahl angezeigt wird.
 *
 * ``created_at`` steht dabei, seit die Liste nach der Anlage sortiert
 * (Issue #9): Eine Tabelle, deren Reihenfolge sich aus einer Spalte ergibt,
 * die man nicht sieht, ist eine Tabelle ohne erkennbare Ordnung -- und der
 * Sortierpfeil haette nichts, woran er haengen koennte.
 */
export const DEFAULT_COLUMNS: readonly ViewColumn[] = [
  'ticket_number',
  'title',
  'customer',
  'state',
  'priority',
  'queue',
  'sla',
  // F1-50. Sie steht in der Vorgabe, weil sie die Frage beantwortet, mit
  // der man die Liste morgens oeffnet: Wo wartet jemand auf uns?
  'awaiting_reply',
  'created_at',
  'updated_at',
];

/** Die Felder, nach denen der Server sortiert. */
export const SORT_FIELDS = [
  'ticket_number',
  'title',
  'created_at',
  'updated_at',
  'resolution_due_at',
  'awaiting_reply_since',
] as const;

export type SortField = (typeof SORT_FIELDS)[number];

/**
 * Wonach sich eine Spalte sortieren laesst.
 *
 * **Deckungsgleich mit `SORTABLE_FIELDS` in
 * `modules/tickets/repository.py`.** Die Liste dort ist eine Erlaubnisliste
 * und keine Pruefung auf "ist ein Spaltenname": Sonst waere
 * `sort=password_hash` eine gueltige Angabe, und die Reihenfolge der Liste
 * verriete etwas ueber einen Wert, den niemand sehen darf.
 *
 * **Was hier fehlt, ist mit Absicht nicht sortierbar.** Kunde, Status,
 * Prioritaet, Warteschlange, Kategorie und Bearbeiter stehen als Verweis in
 * der Zeile; der Server muesste fuer ihre Reihenfolge verbinden, und ein
 * `ORDER BY state_id` sortierte nach Kennungen -- also nach nichts, was ein
 * Mensch erwartet. Diese Ueberschriften bleiben deshalb Text und werden
 * nicht zu Schaltflaechen: Eine Ueberschrift, die sich anklicken laesst und
 * nichts tut, ist schlechter als eine, die es sichtbar nicht tut.
 */
export const SORT_FIELD_OF: Partial<Record<ViewColumn, SortField>> = {
  ticket_number: 'ticket_number',
  title: 'title',
  created_at: 'created_at',
  updated_at: 'updated_at',
  // Die SLA-Spalte zeigt die Restzeit; sortiert wird nach der Frist, aus der
  // sie sich ergibt.
  sla: 'resolution_due_at',
  // Aufsteigend gelesen: wer wartet am laengsten. Das ist die Reihenfolge,
  // in der eine Arbeitsliste abgearbeitet wird.
  awaiting_reply: 'awaiting_reply_since',
};

/**
 * Die Reihenfolge, in der die Liste ohne eigene Wahl steht.
 *
 * **Nach Anlage und nicht nach Aenderung** (Issue #9): Ein neu angelegtes
 * Ticket soll oben stehen, damit man sieht, was hinzugekommen ist. Mit
 * `-updated_at` rutschte es nach unten, sobald jemand einen aelteren Vorgang
 * anfasste -- und genau dann sieht man das Neue nicht mehr.
 */
export const DEFAULT_SORT = '-created_at';

/** Ob eine Zeichenkette eine zulaessige Sortierangabe ist. */
export function isSortValue(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const field = value.startsWith('-') ? value.slice(1) : value;
  return (SORT_FIELDS as readonly string[]).includes(field);
}
